import '../utilities/stringUtils.js';
import { styled, useTheme } from '@vebox/etui-system';
import '../utilities/format-ms.js';
import '../utilities/create-transition.js';
import { useRef, useState, useMemo, useEffect, createElement } from 'react';
import '../utilities/get-styleprop.js';
import { a as _objectSpread2, b as _extends } from '../_rollupPluginBabelHelpers-3b7bd988.js';
import { Div } from '@vebox/etui-system-elements';
import '../Svg-0180d7b5.js';
import Icon from '../Icon/Icon.js';
import '@vebox/etui-icons';
import { icons } from '../Icon/icons.js';
import '../Icon/helper.js';
import '@vebox/etui-colors';
import '../ErrorLabel/ErrorLabel.js';
import '../FormItem/FormItem.js';
import '../Input/Input.js';
import '../InputLabel/InputLabel.js';
import TextInput from '../TextInput/TextInput.js';
import InputAddon from '../InputAddon/InputAddon.js';
import 'react-dom';
import '../ClickOutside/ClickOutside.js';
import '../Tooltip/Tooltip.js';
import Button from '../Button/Button.js';
import 'date-fns';
import 'react-popper';
import DropdownMenu from '../DropdownMenu/DropdownMenu.js';
import '../DropdownMenu/Item.js';
import '../DropdownMenu/Divider.js';
import { defaultLabels, TIME_FORMAT_12_HOURS, TIME_FORMAT_24_HOURS, ENTER_KEY } from './constants.js';
import { R as RELATIVE_FIELD_POSITION, i as isTimeValid, g as getTimeObject, c as calculateNewTimeValue, a as getDropdownValue, b as getInputErrorConfig, d as isHelperTextVisible, e as getHelperMaskText, f as isTimeDisplayValueValid, h as getTimeString } from '../utils-1a2b02cb.js';
import './Field.js';
import TimePickerPanel from './TimePickerPanel.js';
import './TimeField.js';
import './TimePeriodFormatField.js';

const MaskHelperText = styled(Div)`
  color: ${({
  theme
}) => theme.palette.text.disabled};
  position: absolute;
  left: 42px;
  pointer-events: none;
`;

const TimePicker = ({
  labels = {},
  value = null,
  defaultValue = null,
  useMeridiem,
  disabled,
  onChange,
  name = 'timePicker',
  mode = 'standard',
  relativeFieldPosition = RELATIVE_FIELD_POSITION.RIGHT,
  testId = 'timePicker',
  isReadOnly = false,
  styles = {},
  onValidStateChange,
  isInlineEditMode = true,
  showDefaultError = true,
  onBlur,
  size,
  openByDefault = false,
  onBeforeCancel,
  dropdownProps
}) => {
  var _dropdownProps$styles;
  const componentLabels = _objectSpread2(_objectSpread2({}, defaultLabels), labels);
  const inputRef = useRef(null);
  const theme = useTheme();
  const isRelativeMode = mode === 'relative';
  const [isInputFocused, setInputFocused] = useState(false);
  const [timePickerValue, setTimePickerValue] = useState(value || defaultValue);
  const timeFormat = useMeridiem ? TIME_FORMAT_12_HOURS : TIME_FORMAT_24_HOURS;
  const [isValueValid, setIsValueValid] = useState(isTimeValid(timePickerValue) || isRelativeMode);
  const initialValues = useMemo(() => getTimeObject(value || timePickerValue || defaultValue, useMeridiem), [value, timePickerValue, defaultValue, useMeridiem]);
  const handleInputChange = event => {
    const newDisplayValue = event.target.value;
    if (isTimeDisplayValueValid(newDisplayValue, useMeridiem)) {
      const newTime = calculateNewTimeValue(newDisplayValue, useMeridiem, mode);
      setIsValueValid(true);
      setTimePickerValue(newTime);
      onChange === null || onChange === void 0 ? void 0 : onChange(newTime);
    } else {
      setIsValueValid(false);
      setTimePickerValue(newDisplayValue);
      onChange === null || onChange === void 0 ? void 0 : onChange(newDisplayValue);
    }
  };
  const getDisplayValue = () => {
    const timeValue = value || timePickerValue;
    return isTimeValid(timeValue) ? getDropdownValue({
      timePickerValue: timeValue,
      timeFormat,
      isRelativeMode,
      relativeFieldPosition,
      labels: componentLabels
    }) : timeValue;
  };
  const handleKeyDown = event => {
    if (event.key === ENTER_KEY) {
      var _inputRef$current;
      (_inputRef$current = inputRef.current) === null || _inputRef$current === void 0 ? void 0 : _inputRef$current.blur();
    }
  };
  const handleInputFocus = () => setInputFocused(true);
  const handleInputBlur = event => {
    setInputFocused(false);
    onBlur === null || onBlur === void 0 ? void 0 : onBlur(event);
  };
  useEffect(() => {
    onValidStateChange === null || onValidStateChange === void 0 ? void 0 : onValidStateChange(isValueValid);
  }, [isValueValid]);
  useEffect(() => {
    if (value !== timePickerValue) {
      setTimePickerValue(calculateNewTimeValue(value, useMeridiem, mode));
      setIsValueValid(isTimeValid(value));
    }
  }, [value]);
  useEffect(() => {
    if (defaultValue) {
      setTimePickerValue(calculateNewTimeValue(defaultValue, useMeridiem, mode));
      setIsValueValid(isTimeValid(defaultValue));
    }
  }, []);
  const renderContent = ({
    closeDropdown
  }) => {
    const handleApplyChanges = ({
      hours,
      minutes,
      meridiem,
      relativeMode
    }) => {
      const newValue = getTimeString({
        hours,
        minutes,
        meridiem,
        useMeridiem,
        relativeMode
      });
      onChange === null || onChange === void 0 ? void 0 : onChange(newValue);
      setTimePickerValue(newValue);
      setIsValueValid(isTimeValid(newValue));
      closeDropdown();
    };
    return /*#__PURE__*/createElement(TimePickerPanel, {
      initialHours: initialValues.hours,
      initialMinutes: initialValues.minutes,
      initialMeridiem: initialValues.meridiem,
      initialRelativeMode: initialValues.relativeModeValue,
      relativeFieldPosition: relativeFieldPosition,
      onCancel: closeDropdown,
      onApply: handleApplyChanges,
      useMeridiem: useMeridiem,
      mode: mode,
      labels: componentLabels,
      onBeforeCancel: onBeforeCancel
    });
  };
  const renderStartInputAddon = () => /*#__PURE__*/createElement(InputAddon, {
    position: "start"
  }, /*#__PURE__*/createElement(Icon, {
    glyph: icons.CLOCK,
    size: 24
  }));
  const renderEndInputAddon = (isDropdownOpened, toggleDropdown) => isReadOnly ? null : /*#__PURE__*/createElement(InputAddon, {
    position: "end",
    styles: {
      root: {
        top: 0
      }
    }
  }, /*#__PURE__*/createElement(Button, {
    testId: "openTimePickerButton",
    iconOnly: true,
    disabled: disabled,
    iconProps: {
      glyph: icons.CHEVRON_DOWN,
      rotate: isDropdownOpened ? 180 : 0
    },
    onClick: event => {
      if (!isReadOnly && !disabled) {
        toggleDropdown();
        event.stopPropagation();
      }
    },
    styles: {
      root: {
        px: '0 !important',
        pt: size === 'small' ? `${theme.spacing.sm}px!important` : `${theme.spacing.md}px!important`
      }
    }
  }));
  const isEditableDefaultMode = !isInlineEditMode && !disabled;
  const isFullAreaClickable = !isReadOnly && (isRelativeMode || isEditableDefaultMode);
  const displayValue = getDisplayValue();
  return /*#__PURE__*/createElement(DropdownMenu, _extends({
    isDisabled: disabled,
    testIds: {
      button: testId,
      contentContainer: `${testId}-content`
    },
    portalContainer: dropdownProps === null || dropdownProps === void 0 ? void 0 : dropdownProps.portalContainer
  }, dropdownProps, {
    styles: _objectSpread2(_objectSpread2({}, dropdownProps === null || dropdownProps === void 0 ? void 0 : dropdownProps.styles), {}, {
      menuContainer: _objectSpread2({
        width: 'auto',
        zIndex: 2
      }, dropdownProps === null || dropdownProps === void 0 ? void 0 : (_dropdownProps$styles = dropdownProps.styles) === null || _dropdownProps$styles === void 0 ? void 0 : _dropdownProps$styles.menuContainer)
    }),
    renderButton: ({
      closeDropdown,
      isDropdownOpened,
      toggleDropdown,
      ref
    }) => {
      return /*#__PURE__*/createElement(Div, {
        position: "relative",
        onClick: () => {
          if (isFullAreaClickable) {
            toggleDropdown();
          }
        }
      }, /*#__PURE__*/createElement(TextInput
      // @ts-ignore
      , {
        elRef: el => {
          if (typeof ref === 'function') {
            ref(el);
          }
          inputRef.current = el;
        },
        readOnly: (isReadOnly || !isInlineEditMode || isRelativeMode) && !disabled,
        autoComplete: "off",
        name: name,
        testId: testId,
        label: componentLabels.main,
        value: displayValue,
        defaultValue: displayValue,
        size: size,
        disabled: disabled,
        onChange: handleInputChange,
        onClick: closeDropdown,
        onFocus: handleInputFocus,
        onBlur: handleInputBlur,
        onKeyDown: handleKeyDown,
        error: showDefaultError ? getInputErrorConfig(timePickerValue, isValueValid, componentLabels) : undefined,
        styles: {
          root: _objectSpread2(_objectSpread2({}, isFullAreaClickable ? {
            bg: theme.palette.common.white,
            border: `1px solid ${theme.palette.grey[60]}`
          } : {}), {}, {
            height: size === 'small' ? '42px' : '50px',
            cursor: isFullAreaClickable ? 'pointer' : 'normal'
          }, styles.root),
          input: _objectSpread2({}, styles.input),
          label: _objectSpread2({}, styles.label)
        },
        startInputAddon: renderStartInputAddon(),
        endInputAddon: renderEndInputAddon(isDropdownOpened, toggleDropdown)
      }), isHelperTextVisible(isInputFocused, displayValue) && /*#__PURE__*/createElement(MaskHelperText, {
        top: size === 'small' ? '18px' : '24px'
      }, getHelperMaskText(useMeridiem)));
    },
    renderList: renderContent,
    openByDefault: openByDefault
  }));
};

export default TimePicker;
